import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  Spinner,
  Text,
  Alert,
  AlertIcon,
  useColorModeValue,
} from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { MdArrowBack } from "react-icons/md";
import Card from "components/card/Card";
import { Mark } from "components/icons/Mark";
import { getPartnerTicketById } from "services/partnerTicketsService";
import type { PartnerTicket } from "types/partnerTicket";
import { formatDateTime } from "utils/formatDate";

function statusLabel(status: string): string {
  const map: Record<string, string> = {
    open: "Aberto",
    in_progress: "Em atendimento",
    resolved: "Resolvido",
    closed: "Encerrado",
  };
  return map[status] ?? status;
}

export default function SupportTicketDetail() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<PartnerTicket | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const headingColor = useColorModeValue("secondaryGray.900", "white");
  const mutedColor = useColorModeValue("secondaryGray.600", "secondaryGray.600");
  const borderColor = useColorModeValue("gray.100", "navy.700");

  useEffect(() => {
    if (!ticketId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const t = await getPartnerTicketById(ticketId);
        if (!cancelled) setTicket(t);
      } catch (e: unknown) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Chamado não encontrado.");
          setTicket(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ticketId]);

  if (loading) {
    return (
      <Flex justify="center" py={16}>
        <Spinner size="xl" color="brand.500" thickness="4px" />
      </Flex>
    );
  }

  if (error || !ticket) {
    return (
      <Box>
        <Button
          leftIcon={<Mark as={MdArrowBack} />}
          variant="ghost"
          mb={4}
          onClick={() => navigate("/partner/support")}
        >
          Voltar
        </Button>
        <Alert status="error" borderRadius="xl">
          <AlertIcon />
          {error ?? "Chamado não encontrado."}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Button
        leftIcon={<Mark as={MdArrowBack} />}
        variant="ghost"
        mb={4}
        onClick={() => navigate("/partner/support")}
      >
        Voltar aos chamados
      </Button>

      <Card mb={6}>
        <Heading size="md" color={headingColor} mb={2}>
          {ticket.subject}
        </Heading>
        <Text fontSize="sm" color={mutedColor} mb={4}>
          {statusLabel(ticket.status)} ·{" "}
          {ticket.category === "order" ? "Pedido" : "Plataforma"} · Criado em{" "}
          {formatDateTime(ticket.createdAt)}
        </Text>
        <Box borderTopWidth="1px" borderColor={borderColor} pt={4}>
          <Text fontSize="sm" fontWeight="semibold" color={headingColor} mb={1}>
            Descrição
          </Text>
          <Text fontSize="sm" color={mutedColor} whiteSpace="pre-wrap">
            {ticket.description}
          </Text>
          {ticket.relatedTaskId ? (
            <Text fontSize="sm" color={mutedColor} mt={4}>
              Pedido vinculado: <strong>{ticket.relatedTaskId}</strong>
            </Text>
          ) : null}
        </Box>
        <Box mt={4} p={3} borderRadius="md" bg="brand.50" _dark={{ bg: "navy.800" }}>
          <Text fontSize="sm" color={headingColor} fontWeight="medium">
            Prazo de atendimento (SLA)
          </Text>
          <Text fontSize="sm" color={mutedColor}>
            Até {formatDateTime(ticket.slaDueAt)} ({ticket.slaBusinessDays} dias úteis após a abertura,
            horário de Brasília).
          </Text>
        </Box>
      </Card>
    </Box>
  );
}
